import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MarketingShell } from "../components/MarketingShell";
import { getPublicSiteOrigin } from "../api/client";

export default function PrivacyPolicyPage() {
  const origin = getPublicSiteOrigin() || "https://setpilot.org";
  const url = `${origin}/privacy-policy`;
  const title = "Privacy policy — SetPilot";
  const description =
    "How SetPilot handles session data, uploads, analytics, and your choices when using the DJ set planning tool.";

  return (
    <MarketingShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <article className="m-prose">
        <h1>Privacy policy</h1>
        <p className="m-lede">
          SetPilot (&quot;we&quot;, &quot;us&quot;) helps DJs plan sets in the browser. This policy describes what we collect,
          why, and your choices. Last updated: April 2026.
        </p>

        <h2>What SetPilot is</h2>
        <p>
          SetPilot is a session-based web app: you upload audio files (or import from supported playlists where offered),
          we analyze them to suggest order and transitions, and you can export or share results. We do not require an
          account to use the core product.
        </p>

        <h2>Information we process</h2>
        <ul>
          <li>
            <strong>Session and technical data.</strong> We use a browser session identifier so your uploads and
            generated sets stay grouped for your visit. Standard server and hosting logs may include IP address, user
            agent, timestamps, and requested URLs.
          </li>
          <li>
            <strong>Audio and metadata.</strong> Files you upload are processed for analysis (e.g. BPM, key estimates).
            Retention follows our published session policy; data may be removed after a limited period when the session
            ends or on a schedule we configure for operations and cost.
          </li>
          <li>
            <strong>Optional contact.</strong> If you submit an email for downloads, sharing, or feedback, we use it
            only for that purpose unless we say otherwise at collection.
          </li>
          <li>
            <strong>Advertising and measurement.</strong> If we use third-party advertising or analytics (for example
            Google AdSense or similar), those providers may set or read cookies and process data as described in their
            policies. We will update this page when such services are enabled.
          </li>
        </ul>

        <h2>Cookies and similar technologies</h2>
        <p>
          We may use cookies or local storage for session continuity, preferences, and (where enabled) analytics or ads.
          You can control cookies through your browser settings; blocking some cookies may limit features.
        </p>

        <h2>Third-party services</h2>
        <p>
          The site is hosted on infrastructure providers (for example Vercel for the frontend and a cloud host for APIs).
          Those providers process data on our behalf under their terms and privacy policies.
        </p>

        <h2>Children</h2>
        <p>SetPilot is not directed at children under 13, and we do not knowingly collect their personal information.</p>

        <h2>Your choices</h2>
        <p>
          You may stop using the site at any time. Where applicable law gives you rights to access, correct, delete, or
          restrict processing of personal data, contact us and we will respond within a reasonable time.
        </p>

        <h2>International users</h2>
        <p>
          If you access SetPilot from outside the United States, your information may be processed in the United States or
          other countries where we or our vendors operate.
        </p>

        <h2>Changes</h2>
        <p>
          We may update this policy. We will post the new version on this page and adjust the &quot;Last updated&quot; date.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about privacy: see our <Link to="/contact">contact page</Link>.
        </p>

        <p style={{ marginTop: 28 }}>
          <Link to="/">← Back to the app</Link>
        </p>
      </article>
    </MarketingShell>
  );
}
